/**
 * CrossReferencesTab — review grouping suggestions (possible duplicate
 * items) and re-point purchase options between items.
 *
 * Sections:
 *   1. Suggested vendor-item groupings — pending rows from the identity matcher;
 *      accept moves the duplicate's active purchase options onto the primary
 *      item and archives the duplicate, dismiss keeps both items.
 *   2. Group / ungroup products — pick an item, see its purchase options,
 *      move an option to another item or split it off into a standalone item,
 *      and read the per-item audit trail (item_cross_reference_events).
 *
 * The backing RPCs/tables may not be deployed everywhere yet — the services
 * degrade gracefully, so this tab stays quiet instead of crashing.
 *
 * Props:
 *   storeId   - current store id
 *   items     - the inventory item list the host already holds (used as the
 *               picker catalog; never refetched here)
 *   onChanged? - called after every successful mutation (lists already
 *               refreshed) so hosts can reload derived data
 *   showRelationships? - keep the legacy typed-relationship review section;
 *               pass false when this component is mounted as the separate
 *               Vendor Item Matching workflow
 *   onOpenRelatedCatalog? - navigate to the Relationships catalog view;
 *               when provided, the already-confirmed summary line links there
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchSuggestions,
  acceptSuggestion,
  dismissSuggestion,
  attachOptionToItem,
  detachOptionToNewItem,
  fetchEventLog,
} from '../services/itemCrossReferenceService';
import {
  fetchRelationshipsForStore,
  confirmRelationship,
  rejectRelationship,
} from '../services/itemRelationshipService';
import { fetchOptionsForItems } from '../services/purchaseOptionService';
import { formatOptionLabel } from '../utils/purchaseOptions';
import { RELATIONSHIP_TYPE_LABELS, materialDifferencesLabel } from '../utils/relatedProductCatalog';

const PICKER_RESULT_LIMIT = 100;

const badgeStyle = ({ bg, fg }) => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 999,
  background: bg,
  color: fg,
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  whiteSpace: 'nowrap',
});

const styles = {
  wrap: { fontFamily: 'Open Sans, sans-serif', color: '#3D2518', fontSize: 13 },
  sectionTitle: { fontSize: 14, fontWeight: 700, color: '#5A4A42', margin: '0 0 6px' },
  sectionHint: { color: '#8B7B73', fontSize: 12, margin: '0 0 10px' },
  section: { marginBottom: 18 },
  muted: { color: '#8B7B73', fontSize: 12 },
  card: {
    background: '#fff',
    border: '1.5px solid #DCCFC2',
    borderRadius: 10,
    padding: '10px 12px',
    marginBottom: 10,
  },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' },
  cardTitle: { fontWeight: 700, fontSize: 13, color: '#3D2518' },
  badges: { display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' },
  label: { color: '#6B5A52', fontSize: 12, marginTop: 4 },
  actions: { display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 },
  smallBtn: {
    display: 'inline-flex', alignItems: 'center', minHeight: 28, padding: '4px 10px',
    borderRadius: 8, fontWeight: 600, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit',
    background: '#fff', color: '#5A4A42', border: '1.5px solid #d5c9c0', whiteSpace: 'nowrap',
  },
  amberBtn: {
    display: 'inline-flex', alignItems: 'center', minHeight: 28, padding: '4px 10px',
    borderRadius: 8, fontWeight: 700, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit',
    background: '#FFF4E0', color: '#8A5A00', border: '1.5px solid #E6B866', whiteSpace: 'nowrap',
  },
  greenBtn: {
    display: 'inline-flex', alignItems: 'center', minHeight: 28, padding: '4px 10px',
    borderRadius: 8, fontWeight: 700, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit',
    background: '#E8F5E9', color: '#2E7D32', border: '1.5px solid #C8E6C9', whiteSpace: 'nowrap',
  },
  error: {
    padding: '8px 12px', color: '#b71c1c', background: '#FDECEA', borderRadius: 8,
    border: '1px solid #f5c2bd', fontSize: 12, marginBottom: 10,
  },
  notice: {
    padding: '8px 12px', color: '#2E7D32', background: '#E8F5E9', borderRadius: 8,
    border: '1px solid #C8E6C9', fontSize: 12, marginBottom: 10,
  },
  field: { display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 8 },
  fieldLabel: { fontSize: 10, fontWeight: 700, color: '#5A4A42', textTransform: 'uppercase', letterSpacing: '0.05em' },
  input: {
    padding: '7px 9px', border: '1.5px solid #E9DFD6', borderRadius: 8,
    fontSize: 13, color: '#3D2518', background: '#fff', fontFamily: 'inherit',
  },
  movePanel: {
    background: '#FBF8F5', border: '1.5px dashed #DCCFC2', borderRadius: 10,
    padding: '10px 12px', marginTop: 8,
  },
  // Already-confirmed relationships never appear in the review list below —
  // strong matches (>= 90% confidence) auto-confirm during import. Without
  // this line a successful import reads as "matching found nothing".
  confirmedSummary: {
    display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap',
    background: '#FBF8F5', border: '1px solid #EDE4DA', borderRadius: 8,
    padding: '8px 10px', fontSize: 12, color: '#5A4A42', marginBottom: 10,
  },
  linkBtn: {
    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
    fontFamily: 'inherit', fontSize: 12, fontWeight: 700, color: '#8A5A00',
    textDecoration: 'underline', whiteSpace: 'nowrap',
  },
  // Purchasing-alternative cards are deliberately NOT the merge cards: dashed
  // border + parchment background say "these stay two items" at a glance.
  altCard: {
    background: '#FBF8F5',
    border: '1.5px dashed #C9BBAE',
    borderRadius: 10,
    padding: '10px 12px',
    marginBottom: 10,
  },
  altDifferences: { color: '#6B5A52', fontSize: 12, marginTop: 4 },
  historyBox: {
    marginTop: 8, background: '#FBF8F5', border: '1px solid #EDE4DA', borderRadius: 8,
    padding: '8px 10px', fontSize: 12, color: '#5A4A42',
  },
  historyRow: { padding: '3px 0', borderBottom: '1px solid #F1EAE2' },
  aiBlock: {
    marginTop: 4, background: '#F7F4FB', border: '1px solid #E3DDF0', borderRadius: 8,
    padding: '6px 8px', fontSize: 12, color: '#6B5A52',
  },
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString();
};

// Matcher scores live in [0, 1] — show them as a percentage.
const scoreLabel = (score) => {
  const parsed = Number.parseFloat(score);
  if (!Number.isFinite(parsed)) return null;
  return parsed <= 1 ? `${Math.round(parsed * 100)}%` : String(parsed);
};

const itemLabel = (item) => {
  if (!item) return '';
  const extras = [item.brand, item.vendor].filter(Boolean).join(' · ');
  return extras ? `${item.description} (${extras})` : (item.description || `Item ${item.id}`);
};

// AI-authored suggestions (items-ai-match edge function) carry an
// 'ai:'-prefixed free-text reason instead of a matcher reason code; strip the
// prefix for display. Returns null for non-AI reasons.
const aiReasonText = (reason) => {
  const text = String(reason || '');
  return text.startsWith('ai:') ? text.slice(3).trim() : null;
};

// Rejected-alternatives list the AI considered, when the row carries one
// (the edge function may store it as `rejected` or inside `detail`).
const aiRejectedList = (suggestion) => {
  const list = Array.isArray(suggestion?.rejected)
    ? suggestion.rejected
    : (Array.isArray(suggestion?.detail?.rejected) ? suggestion.detail.rejected : []);
  return list.filter((entry) => entry && entry.inventory_item_id !== null && entry.inventory_item_id !== undefined);
};

const matchesQuery = (item, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [item.description, item.brand, item.vendor, item.category]
    .filter(Boolean)
    .some((field) => String(field).toLowerCase().includes(q));
};

/** Search input + filtered select over the host-provided item list. */
const ItemPicker = ({ idPrefix, items, value, query, onQueryChange, onChange, excludeId, placeholder }) => {
  const filtered = items
    .filter((item) => String(item.id) !== String(excludeId ?? ''))
    .filter((item) => matchesQuery(item, query))
    .slice(0, PICKER_RESULT_LIMIT);
  return (
    <div>
      <input
        id={`${idPrefix}-search`}
        type="search"
        style={{ ...styles.input, width: '100%', marginBottom: 6 }}
        placeholder="Search items…"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
      />
      <select
        id={`${idPrefix}-select`}
        style={{ ...styles.input, width: '100%' }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{placeholder}</option>
        {filtered.map((item) => (
          <option key={item.id} value={item.id}>{itemLabel(item)}</option>
        ))}
      </select>
      {query.trim() && filtered.length === PICKER_RESULT_LIMIT && (
        <div style={{ ...styles.muted, marginTop: 3 }}>Showing first {PICKER_RESULT_LIMIT} matches — keep typing to narrow.</div>
      )}
    </div>
  );
};

export default function CrossReferencesTab({
  storeId,
  items = [],
  onChanged,
  onOpenRelatedCatalog,
  showRelationships = true,
}) {
  const [suggestions, setSuggestions] = useState(null); // null = loading
  const [relationships, setRelationships] = useState(null); // null = loading
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const [pickerQuery, setPickerQuery] = useState('');
  const [selectedItemId, setSelectedItemId] = useState('');
  const [options, setOptions] = useState(null); // null = loading

  const [moveFor, setMoveFor] = useState(null); // option id with the move panel open
  const [moveQuery, setMoveQuery] = useState('');
  const [moveTargetId, setMoveTargetId] = useState('');

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const itemsById = useMemo(() => {
    const map = new Map();
    (items || []).forEach((item) => map.set(String(item.id), item));
    return map;
  }, [items]);

  // Relationships that already graduated past this review screen. not_related
  // is a stored "no", not a link — it never counts as a confirmed
  // relationship.
  const confirmedRelationshipCount = useMemo(() => (
    (relationships || []).filter((row) =>
      row.status === 'confirmed' && row.relationship_type !== 'not_related').length
  ), [relationships]);

  const selectedItem = selectedItemId ? itemsById.get(String(selectedItemId)) : null;

  const loadSuggestions = useCallback(async () => {
    if (!storeId) {
      setSuggestions([]);
      return;
    }
    try {
      const rows = await fetchSuggestions(storeId);
      setSuggestions(rows || []);
      setError('');
    } catch (err) {
      setSuggestions([]);
      setError(err?.message || 'Failed to load suggestions.');
    }
  }, [storeId]);

  useEffect(() => {
    setSuggestions(null);
    loadSuggestions();
  }, [loadSuggestions]);

  // Purchasing alternatives ride the same tab but a DIFFERENT table:
  // item_relationships rows never merge anything. The service returns [] when
  // the migration is not deployed, so the panel stays quiet.
  const loadRelationships = useCallback(async () => {
    if (!storeId || !showRelationships) {
      setRelationships([]);
      return;
    }
    try {
      const rows = await fetchRelationshipsForStore(storeId);
      setRelationships(rows || []);
    } catch (err) {
      setRelationships([]);
      setError(err?.message || 'Failed to load purchasing alternatives.');
    }
  }, [showRelationships, storeId]);

  useEffect(() => {
    setRelationships(null);
    loadRelationships();
  }, [loadRelationships]);

  const loadOptions = useCallback(async () => {
    if (!storeId || !selectedItemId) {
      setOptions([]);
      return;
    }
    try {
      const grouped = await fetchOptionsForItems(storeId, [selectedItemId]);
      setOptions(grouped[selectedItemId] || []);
      setError('');
    } catch (err) {
      setOptions([]);
      setError(err?.message || 'Failed to load purchase options.');
    }
  }, [storeId, selectedItemId]);

  useEffect(() => {
    setOptions(selectedItemId ? null : []);
    setMoveFor(null);
    setMoveQuery('');
    setMoveTargetId('');
    setHistoryOpen(false);
    setHistoryRows([]);
    loadOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadOptions]);

  const runMutation = async (action) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const result = await action();
      await Promise.all([loadSuggestions(), loadOptions()]);
      if (typeof onChanged === 'function') onChanged();
      return { ok: true, result };
    } catch (err) {
      setError(err?.message || 'Action failed. Please try again.');
      return { ok: false, result: null };
    } finally {
      setBusy(false);
    }
  };

  const suggestionNames = (suggestion) => ({
    duplicate: suggestion.duplicate_description || `item ${suggestion.duplicate_item_id}`,
    primary: suggestion.primary_description || `item ${suggestion.primary_item_id}`,
  });

  const handleAccept = async (suggestion) => {
    const { duplicate, primary } = suggestionNames(suggestion);
    // Accepting LINKS the pair as the same product — both items survive with
    // their own vendors and prices. Nothing moves and nothing is archived.
    if (!window.confirm(
      `Link "${duplicate}" and "${primary}" as the same product? Both items are kept and appear together as one related group.`,
    )) return;
    const { ok, result } = await runMutation(() => acceptSuggestion(suggestion.id));
    if (!ok) return;
    if (result && typeof result === 'object' && result.dismissed_inactive) {
      setNotice(
        `Couldn't link "${duplicate}" with "${primary}" — one side is no longer active, so the suggestion was dismissed.`,
      );
    } else {
      setNotice(`Linked "${duplicate}" and "${primary}" as the same product — both items kept.`);
    }
  };

  const handleDismiss = (suggestion) => runMutation(() => dismissSuggestion(suggestion.id));

  // Relationship actions are optimistic: the row answers instantly and the
  // RPC reconciles behind it; a failure surfaces the error and reloads truth.
  const relationshipItemName = (relationship, itemId) => {
    const embeddedItem = String(relationship?.item_a_id) === String(itemId)
      ? relationship?.item_a
      : relationship?.item_b;
    return itemsById.get(String(itemId))?.description
      || embeddedItem?.description
      || `item ${itemId}`;
  };

  const handleConfirmRelationship = async (relationship) => {
    setRelationships((prev) => (prev || []).map((row) => (
      row.id === relationship.id ? { ...row, status: 'confirmed' } : row
    )));
    try {
      await confirmRelationship(relationship.id);
      setNotice(
        `Recorded: "${relationshipItemName(relationship, relationship.item_a_id)}" and "${relationshipItemName(relationship, relationship.item_b_id)}" are related — both items stay.`,
      );
      if (typeof onChanged === 'function') onChanged();
    } catch (err) {
      setError(err?.message || 'Failed to confirm the relationship.');
      loadRelationships();
    }
  };

  const handleRejectRelationship = async (relationship) => {
    setRelationships((prev) => (prev || []).filter((row) => row.id !== relationship.id));
    try {
      await rejectRelationship(relationship.id);
      if (typeof onChanged === 'function') onChanged();
    } catch (err) {
      setError(err?.message || 'Failed to update the relationship.');
      loadRelationships();
    }
  };

  const handleMoveConfirm = async (option) => {
    if (!moveTargetId) return;
    const target = itemsById.get(String(moveTargetId));
    const targetLabel = itemLabel(target) || `item ${moveTargetId}`;
    const { ok } = await runMutation(() =>
      attachOptionToItem(option.id, target?.id ?? moveTargetId, { channel: 'editor' }));
    if (ok) {
      setNotice(`Moved "${formatOptionLabel(option) || 'purchase option'}" to "${targetLabel}".`);
      setMoveFor(null);
      setMoveQuery('');
      setMoveTargetId('');
    }
  };

  const handleDetach = async (option) => {
    const label = formatOptionLabel(option) || 'this purchase option';
    if (!window.confirm(
      `Make ${label} a standalone inventory item? A new item is created and the option moves to it.`,
    )) return;
    const { ok, result } = await runMutation(() => detachOptionToNewItem(option.id));
    if (!ok) return;
    const newItemId = result?.new_item_id;
    setNotice(newItemId
      ? `Created standalone item ${newItemId} and moved "${label}" to it.`
      : `Created a standalone item for "${label}".`);
  };

  const toggleHistory = async () => {
    if (historyOpen) {
      setHistoryOpen(false);
      return;
    }
    setHistoryOpen(true);
    if (!storeId || !selectedItemId) {
      setHistoryRows([]);
      return;
    }
    setHistoryLoading(true);
    try {
      const rows = await fetchEventLog(storeId, { inventoryItemId: selectedItem?.id ?? selectedItemId });
      setHistoryRows(rows || []);
    } catch (err) {
      setHistoryRows([]);
      setError(err?.message || 'Failed to load cross-reference history.');
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <div style={styles.wrap}>
      {error && <div style={styles.error} role="alert">{error}</div>}
      {notice && <div style={styles.notice} role="status">{notice}</div>}

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Suggested vendor-item groupings</h3>
        <p style={styles.sectionHint}>
          Items that look like the same product bought from different vendors or pack sizes.
          Grouping keeps the primary item and moves the duplicate&apos;s vendor options onto it.
        </p>
        {suggestions === null ? (
          <div style={styles.muted}>Loading suggestions…</div>
        ) : suggestions.length === 0 ? (
          <div style={styles.muted}>No suggested groupings right now.</div>
        ) : (
          suggestions.map((suggestion) => {
            const { duplicate, primary } = suggestionNames(suggestion);
            const score = scoreLabel(suggestion.score);
            const aiText = aiReasonText(suggestion.reason);
            const aiRejected = aiText !== null ? aiRejectedList(suggestion) : [];
            return (
              <div key={suggestion.id} style={styles.card} data-testid="xref-suggestion-card">
                <div style={styles.cardTop}>
                  <div>
                    <span style={styles.cardTitle}>&ldquo;{duplicate}&rdquo; looks like &ldquo;{primary}&rdquo;</span>
                    {aiText !== null ? (
                      <div style={styles.aiBlock} data-testid="xref-ai-reason">
                        <span style={badgeStyle({ bg: '#EDE7F6', fg: '#5E35B1' })}>AI suggested</span>
                        {aiText && <div style={{ marginTop: 4 }}>{aiText}</div>}
                        {aiRejected.length > 0 && (
                          <div style={{ marginTop: 4 }}>
                            Also considered and rejected:
                            <ul style={{ margin: '2px 0 0', paddingLeft: 16 }}>
                              {aiRejected.map((entry, idx) => (
                                <li key={entry.inventory_item_id ?? idx}>
                                  {itemsById.get(String(entry.inventory_item_id))?.description || `item ${entry.inventory_item_id}`}
                                  {entry.reason ? ` — ${entry.reason}` : ''}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={styles.label}>
                        {suggestion.reason ? `Reason: ${suggestion.reason}` : 'Possible duplicate item'}
                      </div>
                    )}
                  </div>
                  <div style={styles.badges}>
                    {score && <span style={badgeStyle({ bg: '#E3F2FD', fg: '#0d47a1' })}>{score} match</span>}
                    {suggestion.channel && (
                      <span style={badgeStyle({ bg: '#F5F1EC', fg: '#8B7B73' })}>{suggestion.channel}</span>
                    )}
                  </div>
                </div>
                <div style={styles.actions}>
                  <button
                    type="button"
                    style={styles.amberBtn}
                    disabled={busy}
                    onClick={() => handleAccept(suggestion)}
                  >
                    Group together
                  </button>
                  <button
                    type="button"
                    style={styles.smallBtn}
                    disabled={busy}
                    onClick={() => handleDismiss(suggestion)}
                  >
                    Keep separate
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {showRelationships && <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Potential purchasing alternatives &amp; related products</h3>
        <p style={styles.sectionHint}>
          Different products that can cover the same need — 36% vs 40% cream, 16/20 vs 21/25 shrimp —
          plus products flagged as the same item bought through a different vendor or package.
          Confirming records the relationship and keeps BOTH items; it never merges anything. That is
          what makes these different from the grouping suggestions above.
        </p>
        {confirmedRelationshipCount > 0 && (
          <div style={styles.confirmedSummary} data-testid="xref-confirmed-summary" role="status">
            <span>
              {confirmedRelationshipCount} relationship{confirmedRelationshipCount === 1 ? ' is' : 's are'} already
              confirmed — strong matches (90%+ confidence) are confirmed automatically and never wait here.
            </span>
            {typeof onOpenRelatedCatalog === 'function' && (
              <button type="button" style={styles.linkBtn} onClick={onOpenRelatedCatalog}>
                View related items
              </button>
            )}
          </div>
        )}
        {relationships === null ? (
          <div style={styles.muted}>Loading purchasing alternatives…</div>
        ) : (() => {
          // Every positive suggested type renders — including same_item, which
          // used to be excluded here AND filtered out of the Related Products
          // catalog strip, leaving those suggestions visible on no surface at
          // all. A same_item suggestion below merge-safety still deserves a
          // human answer; confirming it records the relationship and never
          // merges (the merge queue above stays the only path to a merge).
          const alternatives = (relationships || []).filter((row) =>
            row.status === 'suggested'
            && (row.relationship_type === 'purchasing_alternative'
              || row.relationship_type === 'same_family'
              || row.relationship_type === 'same_item'));
          if (alternatives.length === 0) {
            return <div style={styles.muted}>No suggested purchasing alternatives right now.</div>;
          }
          return alternatives.map((relationship) => {
            const score = scoreLabel(relationship.confidence);
            const differences = materialDifferencesLabel(relationship.material_differences);
            return (
              <div key={relationship.id} style={styles.altCard} data-testid="xref-alternative-card">
                <div style={styles.cardTop}>
                  <div>
                    <span style={styles.cardTitle}>
                      &ldquo;{relationshipItemName(relationship, relationship.item_a_id)}&rdquo; ↔ &ldquo;{relationshipItemName(relationship, relationship.item_b_id)}&rdquo;
                    </span>
                    {differences && (
                      <div style={styles.altDifferences}>Differences: {differences}</div>
                    )}
                    {relationship.substitution_notes && (
                      <div style={styles.altDifferences}>{relationship.substitution_notes}</div>
                    )}
                    {relationship.reasoning && !relationship.substitution_notes && (
                      <div style={styles.altDifferences}>{relationship.reasoning}</div>
                    )}
                  </div>
                  <div style={styles.badges}>
                    <span style={badgeStyle({ bg: '#FFF4E0', fg: '#8A5A00' })}>
                      {RELATIONSHIP_TYPE_LABELS[relationship.relationship_type] || relationship.relationship_type}
                    </span>
                    {score && <span style={badgeStyle({ bg: '#E3F2FD', fg: '#0d47a1' })}>{score}</span>}
                  </div>
                </div>
                <div style={styles.actions}>
                  <button
                    type="button"
                    style={styles.greenBtn}
                    disabled={busy}
                    onClick={() => handleConfirmRelationship(relationship)}
                  >
                    Confirm relationship
                  </button>
                  <button
                    type="button"
                    style={styles.smallBtn}
                    disabled={busy}
                    onClick={() => handleRejectRelationship(relationship)}
                  >
                    Not related
                  </button>
                </div>
              </div>
            );
          });
        })()}
      </div>}

      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Group / ungroup products</h3>
        <p style={styles.sectionHint}>
          Pick an item to see its vendor options, move a purchase option to the item it really belongs
          to, or split an option off into its own item.
        </p>
        <div style={styles.field}>
          <label style={styles.fieldLabel} htmlFor="xref-item-search">Item</label>
          <ItemPicker
            idPrefix="xref-item"
            items={items || []}
            value={selectedItemId}
            query={pickerQuery}
            onQueryChange={setPickerQuery}
            onChange={setSelectedItemId}
            placeholder="— Select an item —"
          />
        </div>

        {selectedItemId && (
          <div>
            <div style={{ ...styles.muted, marginBottom: 8 }}>
              {options === null
                ? 'Loading purchase options…'
                : `${options.length} purchase option${options.length === 1 ? '' : 's'} on "${itemLabel(selectedItem) || 'this item'}"`}
            </div>

            {(options || []).map((option) => (
              <div key={option.id} style={styles.card} data-testid="xref-option-card">
                <div style={styles.cardTop}>
                  <div>
                    <span style={styles.cardTitle}>{formatOptionLabel(option) || `Option ${option.id}`}</span>
                    <div style={styles.label}>
                      {option.source_type ? `Source: ${option.source_type}` : ''}
                      {option.status && option.status !== 'active' ? `${option.source_type ? ' · ' : ''}${option.status}` : ''}
                    </div>
                  </div>
                  <div style={styles.badges}>
                    {option.is_preferred && <span style={badgeStyle({ bg: '#FFF4E0', fg: '#8A5A00' })}>preferred</span>}
                    {option.status !== 'active' && <span style={badgeStyle({ bg: '#F5F1EC', fg: '#8B7B73' })}>inactive</span>}
                  </div>
                </div>
                <div style={styles.actions}>
                  <button
                    type="button"
                    style={styles.smallBtn}
                    disabled={busy}
                    onClick={() => {
                      setMoveFor(moveFor === option.id ? null : option.id);
                      setMoveQuery('');
                      setMoveTargetId('');
                    }}
                  >
                    {moveFor === option.id ? 'Cancel move' : 'Move to another item'}
                  </button>
                  <button
                    type="button"
                    style={styles.smallBtn}
                    disabled={busy}
                    onClick={() => handleDetach(option)}
                  >
                    Make standalone item
                  </button>
                </div>

                {moveFor === option.id && (
                  <div style={styles.movePanel} data-testid="xref-move-panel">
                    <div style={styles.field}>
                      <label style={styles.fieldLabel} htmlFor="xref-move-search">Move to item</label>
                      <ItemPicker
                        idPrefix="xref-move"
                        items={items || []}
                        value={moveTargetId}
                        query={moveQuery}
                        onQueryChange={setMoveQuery}
                        onChange={setMoveTargetId}
                        excludeId={selectedItemId}
                        placeholder="— Select the target item —"
                      />
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        type="button"
                        style={styles.amberBtn}
                        disabled={busy || !moveTargetId}
                        onClick={() => handleMoveConfirm(option)}
                      >
                        Confirm move
                      </button>
                      <button
                        type="button"
                        style={styles.smallBtn}
                        disabled={busy}
                        onClick={() => { setMoveFor(null); setMoveQuery(''); setMoveTargetId(''); }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {options !== null && options.length === 0 && (
              <div style={{ ...styles.muted, marginBottom: 8 }}>No purchase options on this item yet.</div>
            )}

            <div style={styles.actions}>
              <button
                type="button"
                style={styles.smallBtn}
                onClick={toggleHistory}
                aria-expanded={historyOpen}
              >
                {historyOpen ? 'Hide history' : 'History'}
              </button>
            </div>

            {historyOpen && (
              <div style={styles.historyBox} data-testid="xref-history">
                {historyLoading ? (
                  <div style={styles.muted}>Loading history…</div>
                ) : historyRows.length === 0 ? (
                  <div style={styles.muted}>No cross-reference events recorded for this item.</div>
                ) : (
                  historyRows.map((row, idx) => {
                    const detail = row.detail && typeof row.detail === 'object' ? row.detail : null;
                    const score = scoreLabel(row.score);
                    return (
                      <div key={row.id || idx} style={styles.historyRow}>
                        <strong>{formatDateTime(row.created_at)}</strong>
                        {' · '}{row.event_type || 'event'}
                        {row.channel ? ` · ${row.channel}` : ''}
                        {score ? ` · score ${score}` : ''}
                        {detail && (detail.from_item_id || detail.to_item_id)
                          ? ` · item ${detail.from_item_id ?? '—'} → ${detail.to_item_id ?? '—'}`
                          : ''}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
